/**
 * Editor for a transit segment (flight, train, etc.).
 */

import { Trash2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { TravelItemResponse, TravelTransitMode, TravelFlightStatusValue } from '../api';
import { TRANSIT_MODE_EMOJI } from './TravelHelpers';
import { TravelTransitBookingLinks } from './TravelTransitBookingLinks';
import { useFlightStatus } from '../hooks';

const TRANSIT_MODES: TravelTransitMode[] = [
  'Flight',
  'Train',
  'Bus',
  'Drive',
  'Ferry',
  'Helicopter',
  'Other',
];

interface TravelTransitEditorProps {
  item: TravelItemResponse;
  onUpdateItem: (itemId: string, input: Record<string, unknown>) => Promise<unknown>;
  onDeleteItem: (itemId: string) => Promise<unknown>;
}

export function TravelTransitEditor({
  item,
  onUpdateItem,
  onDeleteItem,
}: TravelTransitEditorProps) {
  const updateField = (field: string, value: string | null) => {
    onUpdateItem(item.itemId, { [field]: value });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Transit Segment</h2>

      {/* From / To */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">From</label>
          <Input
            value={item.transitFrom || ''}
            onChange={(e) => updateField('transitFrom', e.target.value || null)}
            placeholder="Departure city"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">To</label>
          <Input
            value={item.transitTo || ''}
            onChange={(e) => updateField('transitTo', e.target.value || null)}
            placeholder="Arrival city"
          />
        </div>
      </div>

      {/* Mode selector */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Mode</label>
        <select
          value={item.transitMode || ''}
          onChange={(e) => updateField('transitMode', e.target.value || null)}
          className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Select mode…</option>
          {TRANSIT_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {TRANSIT_MODE_EMOJI[mode]} {mode}
            </option>
          ))}
        </select>
      </div>

      {/* Times */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">Departure</label>
          <Input
            value={item.transitDeparture || ''}
            onChange={(e) => updateField('transitDeparture', e.target.value || null)}
            placeholder="14:30"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">Arrival</label>
          <Input
            value={item.transitArrival || ''}
            onChange={(e) => updateField('transitArrival', e.target.value || null)}
            placeholder="16:45"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Notes / Booking Ref</label>
        <Textarea
          value={item.transitNotes || ''}
          onChange={(e) => updateField('transitNotes', e.target.value || null)}
          placeholder="Booking reference, seat number, etc."
          rows={3}
        />
      </div>

      <FlightStatusPanel item={item} />

      <TravelTransitBookingLinks item={item} />

      {/* Delete */}
      <Button
        variant="outline"
        size="sm"
        className="text-destructive border-destructive/30 hover:bg-destructive/10"
        onClick={() => onDeleteItem(item.itemId)}
      >
        <Trash2 className="h-3 w-3 mr-1" /> Remove Transit
      </Button>
    </div>
  );
}

const STATUS_CLASS: Record<TravelFlightStatusValue, string> = {
  unknown: 'bg-muted text-muted-foreground',
  scheduled: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  active: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  landed: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  delayed: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  diverted: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  cancelled: 'bg-destructive/15 text-destructive',
};

const STATUS_LABEL: Record<TravelFlightStatusValue, string> = {
  unknown: 'Unknown',
  scheduled: 'Scheduled',
  active: 'In the air',
  landed: 'Landed',
  delayed: 'Delayed',
  diverted: 'Diverted',
  cancelled: 'Cancelled',
};

function formatTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

function relative(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  const deltaSec = Math.round((Date.now() - d) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}

/**
 * Live flight-status panel — only renders for Flight transits when the
 * `flight_status` feature is on. Hidden entirely otherwise, including when
 * the user has the feature off, so there is no visible affordance to
 * interact with a disabled system.
 */
function FlightStatusPanel({ item }: { item: TravelItemResponse }) {
  const { snapshot, loading, error, refresh, featureEnabled, isFlight } = useFlightStatus(item);

  if (!featureEnabled || !isFlight) return null;

  return (
    <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Live Flight Status
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          onClick={() => void refresh()}
          aria-label="Refresh flight status"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {!snapshot && !loading && (
        <div className="text-xs text-muted-foreground">
          No status yet. Add a flight number like <code>AA100</code> to the notes and refresh.
        </div>
      )}

      {error && <div className="text-xs text-destructive">{error}</div>}

      {snapshot && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASS[snapshot.status ?? 'unknown']}`}
            >
              {STATUS_LABEL[snapshot.status ?? 'unknown']}
            </span>
            {snapshot.flightNumber && (
              <span className="text-sm font-mono">{snapshot.flightNumber}</span>
            )}
            {snapshot.airline && (
              <span className="text-xs text-muted-foreground">({snapshot.airline})</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            {snapshot.departureGate && (
              <div>
                <span className="text-muted-foreground">Dep gate:</span>{' '}
                <span className="font-medium">{snapshot.departureGate}</span>
              </div>
            )}
            {snapshot.arrivalGate && (
              <div>
                <span className="text-muted-foreground">Arr gate:</span>{' '}
                <span className="font-medium">{snapshot.arrivalGate}</span>
              </div>
            )}
            {snapshot.scheduledDeparture && (
              <div>
                <span className="text-muted-foreground">Sched dep:</span>{' '}
                <span className="font-medium">{formatTime(snapshot.scheduledDeparture)}</span>
              </div>
            )}
            {snapshot.actualDeparture && (
              <div>
                <span className="text-muted-foreground">Actual dep:</span>{' '}
                <span className="font-medium">{formatTime(snapshot.actualDeparture)}</span>
              </div>
            )}
            {snapshot.scheduledArrival && (
              <div>
                <span className="text-muted-foreground">Sched arr:</span>{' '}
                <span className="font-medium">{formatTime(snapshot.scheduledArrival)}</span>
              </div>
            )}
            {snapshot.actualArrival && (
              <div>
                <span className="text-muted-foreground">Actual arr:</span>{' '}
                <span className="font-medium">{formatTime(snapshot.actualArrival)}</span>
              </div>
            )}
          </div>

          {snapshot.providerUnavailable && (
            <div className="text-xs text-muted-foreground italic">
              Provider not configured on this deployment — status display is read-only.
            </div>
          )}
          {snapshot.providerMiss && (
            <div className="text-xs text-muted-foreground italic">
              Provider had no record for this flight number + date. Check the flight number.
            </div>
          )}
          {snapshot.providerError && (
            <div className="text-xs text-muted-foreground italic">
              Provider error: {snapshot.providerError}
            </div>
          )}

          <div
            className="text-[10px] text-muted-foreground pt-1 border-t border-border"
            title={snapshot.lastPolledAt ?? ''}
          >
            {snapshot.lastPolledAt
              ? `Updated ${relative(snapshot.lastPolledAt)}`
              : 'Waiting for first poll…'}
          </div>
        </>
      )}
    </div>
  );
}
